import React from 'react';
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState
} from '@microsoft/signalr';
import { useAuth } from 'react-oidc-context';

import { GameContext } from './game';
import { PlayerState, TeamState } from '../types/state';
import { GetFacilitatorChatHub } from '../services/games';
import { ChatState } from '../types/state/websocket/chat-state';
import { sendPushNotification } from '../utils/game-engine/send-push-notification';
import { useParams } from 'react-router-dom';

export interface FacilitatorChatContextProps {
  chatHub: HubConnection | null;
  newChat: ChatState[] | [];
  setNewChat: React.Dispatch<React.SetStateAction<ChatState[] | []>>;
}

const FacilitatorChatContext =
  React.createContext<FacilitatorChatContextProps | null>(null);

const FacilitatorChatProvider = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const { gameCode } = useParams();
  const auth = useAuth();
  const facilitatorId = auth.user?.profile.sub;
  const [game] = React.useContext(GameContext);
  const [newChat, setNewChat] = React.useState<ChatState[] | []>([]);

  const [signalRHub, setSignalRHub] = React.useState<HubConnection | null>(
    null
  );

  const players = React.useRef(game.gameState?.players ?? []);
  const teams = React.useRef(game.gameState?.teams ?? []);

  React.useEffect(() => {
    players.current = game.gameState?.players ?? [];
  }, [game.gameState?.players]);

  React.useEffect(() => {
    teams.current = game.gameState?.teams ?? [];
  }, [game.gameState?.teams]);

  const onChatReceived = React.useCallback(
    (
      data: any,
      group: 'global-chat' | 'player-chat' | 'team-chat' | 'group-chat'
    ) => {
      const { senderCode, senderName, senderAvatar, message, sentDateUtc } =
        data;
      const selectedPlayer = players.current.find((p) => p.code === senderCode);
      if (!selectedPlayer) return;
      const selectedTeam = teams.current.find(
        (t) => t.id === selectedPlayer.teamCode
      );

      let groupId = '';
      if (group === 'group-chat' && selectedTeam) {
        groupId = selectedTeam.groupCode ?? '';
      }

      const chat = {
        group,
        author: {
          id: senderCode,
          name: senderName,
          avatarUrl: senderAvatar,
          ...(group === 'team-chat' && { teamId: selectedPlayer.teamCode }),
          ...(group === 'group-chat' && { groupId })
        },
        timestamp: new Date(sentDateUtc),
        text: message
      };
      setNewChat((prev) => [...prev, chat]);

      if (
        facilitatorId &&
        facilitatorId !== senderCode &&
        'serviceWorker' in navigator &&
        'PushManager' in window
      ) {
        sendPushNotification({
          message,
          avatar: senderAvatar || '/favicon.ico',
          url: `${window.location.origin}/games/${gameCode}/facilitator`,
          playerName: senderName
        });
      }
    },
    [players, teams, facilitatorId, gameCode]
  );

  React.useEffect(() => {
    if (!gameCode || signalRHub) return;
    let isCanceled = false;

    GetFacilitatorChatHub(gameCode).then((response) => {
      const { accessToken, endpointUrl } = response;

      if (accessToken && endpointUrl) {
        const hubConnectionSetup = new HubConnectionBuilder();
        hubConnectionSetup.withUrl(endpointUrl, {
          accessTokenFactory: () => accessToken
        });
        hubConnectionSetup.withAutomaticReconnect();

        const hubConnection = hubConnectionSetup.build();
        hubConnection
          .start()
          .then(() => {
            if (isCanceled) return hubConnection.stop();

            hubConnection.on('GlobalChatReceived', (data) =>
              onChatReceived(data, 'global-chat')
            );
            hubConnection.on('GroupChatReceived', (data) =>
              onChatReceived(data, 'group-chat')
            );
            hubConnection.on('TeamChatReceived', (data) =>
              onChatReceived(data, 'team-chat')
            );
            hubConnection.on('PrivateChatReceived', (data) =>
              onChatReceived(data, 'player-chat')
            );

            setSignalRHub(hubConnection);
          })
          .catch(() => {
            if (isCanceled) return;
          });

        return () => {
          isCanceled = true;

          if (hubConnection.state === HubConnectionState.Connected)
            hubConnection.stop();

          setSignalRHub(null);
        };
      }
    });
  }, [gameCode, onChatReceived]);

  return (
    <FacilitatorChatContext.Provider
      value={{
        chatHub: signalRHub,
        newChat,
        setNewChat
      }}>
      {children}
    </FacilitatorChatContext.Provider>
  );
};

export { FacilitatorChatContext, FacilitatorChatProvider };
